import { hubBody, hubRequest } from "./client";
import type { Role } from "./types";
export type HomeWidget = {
  id: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  config: Record<string, unknown>;
};
export type HomeLayout = { schema: "home/1"; cols: 12; widgets: HomeWidget[] };
export type ConfigSchema = {
  type?: string | string[];
  properties?: Record<string, ConfigSchema>;
  required?: string[];
  additionalProperties?: boolean | ConfigSchema;
  enum?: unknown[];
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  items?: ConfigSchema;
  pattern?: string;
  default?: unknown;
  description?: string;
  anyOf?: ConfigSchema[];
};
export type HomeWidgetDescriptor = {
  type: string;
  name: string;
  description: string;
  minW: number;
  maxW: number;
  minH: number;
  maxH: number;
  configSchema: ConfigSchema;
  tools: string[];
  roles: Role[];
};
export type HomeAuthor = {
  channel: "web" | "mcp" | "http" | "app-bridge";
  kind: "web" | "ai";
  actorId: string;
  keyId?: string;
  keyName?: string;
};
export type HomeState = {
  layout: HomeLayout;
  version: number;
  source: "system" | "role" | "personal";
  defaultRole: Role;
  author: HomeAuthor | null;
  createdAt: string | null;
  changeSummary: string[];
  widgets: HomeWidgetDescriptor[];
};
export type HomeValidation = {
  draftId: string;
  expiresAt: string;
  expectedVersion: number;
  changeSummary: string[];
};
export type HomeHistoryItem = {
  version: number;
  source: HomeState["source"];
  author: HomeAuthor;
  createdAt: string;
  changeSummary: string[];
};
export type HomeHistory = {
  items: HomeHistoryItem[];
  nextCursor: number | null;
  hasMore: boolean;
};
export type HomeDefaults = {
  role: Role;
  version: number;
  layout: HomeLayout;
  author: HomeAuthor | null;
  createdAt: string | null;
  widgets: HomeWidgetDescriptor[];
};
export const getHome = () => hubRequest<HomeState>("/home");
export const getHomeWidgets = () =>
  hubRequest<{ widgets: HomeWidgetDescriptor[] }>("/home/widgets");
export const validateHome = (layout: HomeLayout, expectedVersion: number) =>
  hubRequest<HomeValidation>(
    "/home/validate",
    hubBody({ layout, expectedVersion }),
  );
export const applyHome = (draftId: string, expectedVersion: number) =>
  hubRequest<HomeState>("/home/apply", hubBody({ draftId, expectedVersion }));
export const getHomeHistory = (beforeVersion?: number) =>
  hubRequest<HomeHistory>("/home/history", {}, {
    limit: 20,
    ...(beforeVersion === undefined ? {} : { beforeVersion }),
  });
export const revertHome = (version: number, expectedVersion: number) =>
  hubRequest<HomeState>("/home/revert", hubBody({ version, expectedVersion }));
export const resetHome = (expectedVersion: number) =>
  hubRequest<HomeState>("/home/reset", hubBody({ expectedVersion }));
export const getHomeDefaults = (role: Role) =>
  hubRequest<HomeDefaults>("/home/defaults/" + encodeURIComponent(role));
export const saveHomeDefaults = (
  role: Role,
  layout: HomeLayout,
  expectedVersion: number,
) =>
  hubRequest<HomeDefaults>(
    "/home/defaults/" + encodeURIComponent(role),
    hubBody({ layout, expectedVersion }, "PUT"),
  );
